import React from "react"
import {
    Box,
    Typography,
    Chip,
    LinearProgress,
    Avatar,
    Grid
} from "@mui/material"
import SectionBox from "./SectionBox"
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined"
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined"
import StarIcon from "@mui/icons-material/Star"
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined"
import ViolationPieChart from "./ViolationPieChart"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import ExportAnalyticsReports from "./ExportAnalyticsReports"

export default function AnalyticsAndReportingTab() {
    const violations = [
        {
            id: 1,
            title: "Missing Personal Protective Equipment (PPE)",
            level: "Critical",
            change: "+15%",
            incidents: 45,
            color: "#D92D20",
        },
        {
            id: 2,
            title: "No Valid ID or Work Permit",
            level: "High",
            change: "+8%",
            incidents: 28,
            color: "#F79009",
        },
        {
            id: 3,
            title: "Incorrect Work Uniform",
            level: "Medium",
            change: "-5%",
            incidents: 22,
            color: "#FDB022",
        },
        {
            id: 4,
            title: "Unregistered Worker on Site",
            level: "High",
            change: "+12%",
            incidents: 18,
            color: "#E54D2E",
        },
        {
            id: 5,
            title: "Late Check-in/Early Departure",
            level: "Low",
            change: "-2%",
            incidents: 14,
            color: "#667085",
        },
    ]
    const contractors = [
        {
            name: "BuildRight Inc.",
            rating: 4.8,
            compliance: 96,
            violations: 3,
            projects: 5,
            status: "Excellent",
        },
        {
            name: "ABC Construction",
            rating: 4.2,
            compliance: 87,
            violations: 12,
            projects: 8,
            status: "Good",
        },
        {
            name: "Elite Contractors",
            rating: 3.5,
            compliance: 78,
            violations: 28,
            projects: 6,
            status: "Warning",
        },
        {
            name: "Modern Plumbing",
            rating: 3.1,
            compliance: 71,
            violations: 35,
            projects: 4,
            status: "Poor",
        },
        {
            name: "XYZ Electric",
            rating: 2.8,
            compliance: 65,
            violations: 42,
            projects: 7,
            status: "Critical",
        },
    ]

    const totalIncidents = violations.reduce((a, b) => a + b.incidents, 0)

    const getLevelColor = (level) => {
        switch (level) {
            case "Critical":
                return { bg: "#FEF3F2", color: "#D92D20" }
            case "High":
                return { bg: "#FEF0C7", color: "#B54708" }
            case "Medium":
                return { bg: "#FFF8E1", color: "#B69121" }
            case "Low":
                return { bg: "#ECFDF3", color: "#027A48" }
            default:
                return { bg: "#F9FAFB", color: "#344054" }
        }
    }

    const gradientStops = violations
        .reduce((acc, v, i) => {
            const prev = acc[acc.length - 1]?.end || 0
            const portion = (v.incidents / totalIncidents) * 360
            acc.push({
                color: v.color,
                start: prev,
                end: prev + portion,
                label: v.title.split(" ")[0], // short name for label
                value: v.incidents,
            })
            return acc
        }, [])
        .map((s) => `${s.color} ${s.start}deg ${s.end}deg`)
        .join(", ")


    const getStatusColor = (status) => {
        switch (status) {
            case "Excellent":
                return { color: "#027A48", bg: "#ECFDF3" }
            case "Good":
                return { color: "#6941C6", bg: "#F4EBFF" }
            case "Warning":
                return { color: "#B54708", bg: "#FFFAEB" }
            case "Poor":
                return { color: "#C4320A", bg: "#FFF4ED" }
            case "Critical":
                return { color: "#D92D20", bg: "#FEF3F2" }
            default:
                return { color: "#344054", bg: "#F9FAFB" }
        }
    }

    const violationData = [
        { name: "Missing", value: 45, color: "#FF0D5E" },
        { name: "No", value: 28, color: "#F79009" },
        { name: "Incorrect", value: 22, color: "#F4C015" },
        { name: "Unregistered", value: 18, color: "#FF1E1E" },
        { name: "Late", value: 14, color: "#667085" },
    ]

    const renderStars = (rating) => {
        const stars = []
        const fullStars = Math.floor(rating)
        const halfStar = rating % 1 >= 0.5

        for (let i = 0; i < fullStars; i++) {
            stars.push(<StarIcon key={i} sx={{ color: "#F4B400", fontSize: 18 }} />)
        }
        if (halfStar)
            stars.push(<StarIcon key="half" sx={{ color: "#F4B400", opacity: 0.5, fontSize: 18 }} />)
        while (stars.length < 5)
            stars.push(<StarIcon key={stars.length + "empty"} sx={{ color: "#E4E7EC", fontSize: 18 }} />)
        return stars
    }

    return (
        <Box sx={{ mt: "3vh" }}>
            {/* Filters section goes here */}
            {/* Contractor Compliance Rating */}
            <SectionBox
                icon={<ShieldOutlinedIcon sx={{ fontSize: 22, color: "#101828" }} />}
                title="Contractor Compliance Rating"
                subtitle="Average compliance KPI with violation highlights"
                titleSize="24px"
                action={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PeopleAltOutlinedIcon sx={{ fontSize: 16, color: "#667085" }} />
                        <Typography sx={{ color: "#667085", fontSize: 13 }}>
                            {contractors.length} Contractors
                        </Typography>
                    </Box>
                }
            >
                {/* Table Header */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 2fr 2fr 2fr 1fr 1fr",
                        color: "#667085",
                        fontSize: "13px",
                        fontWeight: 500,
                        mb: 1,
                    }}
                >
                    <Typography>Contractor</Typography>
                    <Typography>Rating</Typography>
                    <Typography>Avg Compliance</Typography>
                    <Typography>Total Violations</Typography>
                    <Typography>Projects</Typography>
                    <Typography>Status</Typography>
                </Box>

                {/* Divider */}
                <Box sx={{ borderBottom: "1px solid #E4E7EC", mb: 1 }} />

                {/* Contractor Rows */}
                {contractors.map((c, index) => {
                    const statusColor = getStatusColor(c.status)
                    return (
                        <Box
                            key={index}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "2fr 2fr 2fr 2fr 1fr 1fr",
                                alignItems: "center",
                                py: 1,
                                borderBottom:
                                    index !== contractors.length - 1
                                        ? "1px solid #E4E7EC"
                                        : "none",
                                "&:hover": {
                                    backgroundColor: "#F9FAFB",
                                    borderRadius: "6px",
                                },
                            }}
                        >
                            {/* Contractor Name */}
                            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#101828" }}>
                                {c.name}
                            </Typography>

                            {/* Rating */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {renderStars(c.rating)}
                                <Typography sx={{ fontSize: "14px", ml: 0.5 }}>
                                    {c.rating.toFixed(1)}
                                </Typography>
                            </Box>

                            {/* Avg Compliance */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                                    {c.compliance}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={c.compliance}
                                    sx={{
                                        flex: 1,
                                        height: 6,
                                        borderRadius: 5,
                                        width: "100px",
                                        maxWidth: "100px",
                                        backgroundColor: "#E4E7EC",
                                        "& .MuiLinearProgress-bar": {
                                            backgroundColor: "#6A00FF",
                                        },
                                    }}
                                />
                            </Box>

                            {/* Total Violations */}
                            <Chip
                                label={c.violations}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    backgroundColor:
                                        c.violations <= 5
                                            ? "#F9FAFB"
                                            : c.violations <= 15
                                                ? "#F2F4F7"
                                                : "#FEE4E2",
                                    color: c.violations > 15 ? "#D92D20" : "#344054",
                                    width: "fit-content",
                                }}
                            />

                            {/* Projects */}
                            <Typography sx={{ fontSize: "14px" }}>{c.projects}</Typography>

                            {/* Status */}
                            <Chip
                                label={c.status}
                                sx={{
                                    backgroundColor: statusColor.bg,
                                    color: statusColor.color,
                                    fontWeight: 500,
                                    fontSize: "13px",
                                    width: "fit-content",
                                }}
                            />
                        </Box>
                    )
                })}
            </SectionBox>
            {/* Left side */}
            <Grid container spacing={0} sx={{
                mt: "3vh",
                display: "flex",
                flexWrap: "nowrap",
                gap: "24px", 
            }}>
                {/* Left side */}
                <Grid item xs={12} md={6} sx={{
                    flex: "1 1 50%",
                    minWidth: 0,
                }}>
                    <SectionBox
                        icon={<WarningAmberOutlinedIcon sx={{ fontSize: 22, color: "#101828" }} />}
                        title="Top Recurring Violations"
                        titleSize="24px"
                    >
                        {violations.map((v, i) => {
                            const levelColor = getLevelColor(v.level)
                            return (
                                <Box
                                    key={v.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        py: 1.2,
                                        borderBottom:
                                            i !== violations.length - 1 ? "1px solid #E4E7EC" : "none",
                                    }}
                                >
                                    {/* Left content */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "14px",
                                                color: "#6A00FF",
                                                minWidth: "28px",
                                            }}
                                        >
                                            #{i + 1}
                                        </Typography>

                                        <Box>
                                            <Typography
                                                sx={{ fontWeight: 500, color: "#101828", fontSize: "14px" }}
                                            >
                                                {v.title}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.5rem",
                                                    mt: "4px",
                                                }}
                                            >
                                                <Chip
                                                    label={v.level}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: levelColor.bg,
                                                        color: levelColor.color,
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        color: v.change.startsWith("+") ? "#D92D20" : "#027A48",
                                                        fontWeight: 500,
                                                        fontSize: "13px",
                                                    }}
                                                >
                                                    {v.change}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Right content */}
                                    <Box sx={{ textAlign: "right" }}>
                                        <Typography
                                            sx={{ fontWeight: 700, fontSize: "16px", color: "#101828" }}
                                        >
                                            {v.incidents}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12px", color: "#667085" }}>
                                            incidents
                                        </Typography>
                                    </Box>
                                </Box>
                            )
                        })}
                    </SectionBox>
                </Grid>

                {/* Right side */}
                <Grid item xs={12} md={6} sx={{
                    flex: "1 1 50%",
                    minWidth: 0,
                }}>
                    <SectionBox
                        title="Violation Distribution"
                        titleSize="24px"
                    >
                        <ViolationPieChart data={violationData} />
                    </SectionBox>
                </Grid>
            </Grid>

            <Box sx={{ mt: "3vh", pb: "3vh" }}>
                <ExportAnalyticsReports />
            </Box>
        </Box>
    )
}
